import { useRef, useState } from "react";
import { Film, Loader2, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgUrl } from "@/lib/api";
import { ImageUploader } from "@/components/common";

export const ImageGrid = ({ images }) => {
  if (!images?.length) return null;
  const cls = images.length === 1 ? "grid-cols-1" : "grid-cols-2";
  return (
    <div className={`mt-3 grid gap-1.5 overflow-hidden rounded-xl ${cls}`}>
      {images.slice(0, 4).map((u, i) => (
        <div key={u} className={`relative overflow-hidden bg-[#EFE9E1] ${images.length === 1 ? "aspect-[4/3]" : images.length === 3 && i === 0 ? "row-span-2 aspect-auto" : "aspect-square"}`}>
          <img src={imgUrl(u)} alt="" className="h-full w-full object-cover" loading="lazy" />
          {i === 3 && images.length > 4 && <span className="absolute inset-0 flex items-center justify-center bg-black/45 text-lg font-semibold text-white">+{images.length - 4}</span>}
        </div>
      ))}
    </div>
  );
};

const MAX_SECONDS = 30;
const CHUNK = 4 * 1024 * 1024;

const durationOf = (file) => new Promise((resolve) => {
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration); };
  v.onerror = () => resolve(0);
  v.src = URL.createObjectURL(file);
});

// Videos go up in 4MB pieces so longer clips get past upload size limits.
const uploadVideo = async (file, onProgress) => {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const total = Math.ceil(file.size / CHUNK);
  for (let i = 0; i < total; i += 1) {
    const fd = new FormData();
    fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
    await api.post(`/uploads/chunked/${id}/part/${i}`, fd);
    onProgress(Math.round(((i + 1) / total) * 100));
  }
  const { data } = await api.post(`/uploads/chunked/${id}/finish`, null, { params: { total, filename: file.name } });
  return data.url;
};

// Up to 4 photos, or 1 video of 30 seconds or less.
export const MediaPicker = ({ images, setImages, video, setVideo }) => {
  const ref = useRef();
  const [pct, setPct] = useState(null);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const d = await durationOf(f);
    if (d > MAX_SECONDS + 0.5) { toast.error(`Videos must be ${MAX_SECONDS} seconds or shorter. This one is ${Math.round(d)} seconds.`); return; }
    setPct(0);
    try { setVideo(await uploadVideo(f, setPct)); } catch (err) { toast.error(errMsg(err)); }
    setPct(null);
  };
  return (
    <div className="space-y-2" data-testid="post-media-picker">
      {!video && <ImageUploader value={images} onChange={setImages} max={4} testid="post-images" />}
      {video && (
        <div className="relative w-full max-w-xs overflow-hidden rounded-xl border bg-black" data-testid="post-video-preview">
          <video src={imgUrl(video)} controls playsInline preload="metadata" className="max-h-64 w-full" />
          <button type="button" onClick={() => setVideo(null)} className="absolute right-1.5 top-1.5 rounded-full bg-black/60 p-1 text-white" data-testid="post-video-remove" aria-label="Remove video"><X size={13} /></button>
        </div>
      )}
      {!images.length && !video && (
        <button type="button" disabled={pct !== null} onClick={() => ref.current.click()} data-testid="post-video-add"
          className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 px-3 py-1.5 text-xs text-slate-600 transition-colors hover:border-slate-500">
          {pct !== null ? <><Loader2 size={14} className="animate-spin" />Uploading {pct}%</> : <><Film size={14} />Add a video (up to 30 seconds)</>}
        </button>
      )}
      <input ref={ref} type="file" accept="video/mp4,video/quicktime,video/webm" hidden onChange={pick} data-testid="post-video-input" />
      <p className="text-xs text-slate-400">Up to 4 photos, or 1 video. Middle screens media before others see it, and checks whether your photos support your fact.</p>
    </div>
  );
};

const PHOTO = {
  supports: ["Photo supports this fact", "bg-emerald-50 text-emerald-800"],
  contradicts: ["Photo appears to contradict this fact", "bg-rose-50 text-rose-700"],
  unrelated: ["Photo doesn't show evidence for this fact", "bg-slate-100 text-slate-600"],
  unclear: ["Middle couldn't tell if the photo supports this fact", "bg-slate-100 text-slate-600"],
};

const MediaNote = ({ p }) => {
  if (p.media_status === "screening") return <p className="mt-2 text-xs text-slate-500" data-testid={`post-media-screening-${p.id}`}>Only you can see this media while Middle screens it.</p>;
  if (p.media_status === "blocked") return <p className="mt-2 rounded-lg bg-rose-50 p-2 text-xs text-rose-700" data-testid={`post-media-blocked-${p.id}`}>Not shown to others: {p.media_block_reason}</p>;
  if (p.video) return <p className="mt-2 text-xs text-slate-500" data-testid={`post-video-note-${p.id}`}>Member's video. Middle screens videos for safety but doesn't fact-check them.</p>;
  const c = p.images?.length && p.media_check && PHOTO[p.media_check.verdict];
  return c ? <p className={`mt-2 inline-flex items-start gap-1.5 rounded-lg px-2 py-1 text-xs ${c[1]}`} title={p.media_check.note} data-testid={`post-photo-check-${p.id}`}><ShieldCheck size={13} className="mt-px shrink-0" />{c[0]}{p.media_check.note ? `: ${p.media_check.note}` : ""}</p> : null;
};

export const PostMedia = ({ p }) => (
  <>
    <ImageGrid images={p.images} />
    {p.video && <video src={imgUrl(p.video)} controls playsInline preload="metadata" className="mt-3 max-h-[480px] w-full rounded-xl bg-black" data-testid={`post-video-${p.id}`} />}
    {(p.images?.length > 0 || p.video || p.media_status === "blocked") && <MediaNote p={p} />}
  </>
);
